/* ============================================================
   YOUTUBE — thin wrapper around the IFrame Player API
   ------------------------------------------------------------
   Loads the API once, builds a single hidden-chrome player, and
   exposes a clean verb set the TV controller drives: tune a
   playlist, skip program +/-, set volume/mute, and query the
   currently-playing program. The native controls are disabled
   so the only way to drive the set is the remote.

   Ported verbatim from the prototype's youtube.js — behavior-identical,
   now typed. See src/engine/yt.d.ts for the minimal YT typings.
   ============================================================ */

import * as Sentry from '@sentry/react'

export interface TubeCallbacks {
  onReady?: () => void
  onStateChange?: (e: YT.OnStateChangeEvent) => void
  onError?: (e: YT.OnErrorEvent) => void
}

export interface TubeOptions {
  subtitles?: boolean
}

export interface Program {
  index: number
  total: number
  title: string
  author: string
  videoId: string
  duration: number
  time: number
}

let readyResolve!: (yt: typeof YT) => void
const readyPromise = new Promise<typeof YT>((res) => {
  readyResolve = res
})
let apiLoaded = false

function loadApi() {
  if (apiLoaded) return
  apiLoaded = true
  const prev = window.onYouTubeIframeAPIReady
  window.onYouTubeIframeAPIReady = () => {
    if (typeof prev === 'function') {
      try {
        prev()
      } catch {
        /* ignore */
      }
    }
    readyResolve(window.YT)
  }
  const tag = document.createElement('script')
  tag.src = 'https://www.youtube.com/iframe_api'
  document.head.appendChild(tag)
}

export class Tube {
  private element: string | HTMLElement
  private container: HTMLElement | null
  private cb: TubeCallbacks
  private player: YT.Player | null = null
  ready = false
  private currentList: string | null = null
  private subtitles = false
  private volume = 70
  private muted = true
  private pendingSeek: number | null = null
  private pendingSeekTimer: number | null = null

  constructor(element: string | HTMLElement, callbacks?: TubeCallbacks, options: TubeOptions = {}) {
    this.element = element
    this.container = typeof element === 'string' ? null : element.parentElement
    this.cb = callbacks || {}
    this.subtitles = Boolean(options.subtitles)
  }

  init(): Promise<Tube> {
    loadApi()
    return readyPromise.then((YTApi) => {
      return new Promise<Tube>((resolve) => {
        // Let the IFrame API build the iframe — it constructs the correct embed
        // URL (avoiding the config error 153 that a hand-built /embed/ URL with
        // enablejsapi + a mismatched origin triggers) AND it already stamps
        // allow="accelerometer; autoplay; encrypted-media; ..." on the frame,
        // which is what delegates the autoplay permission we need. We start
        // muted (mute:1) so muted autoplay is always allowed, then lift the mute
        // synchronously inside the user's first real gesture.
        this.ready = false
        this.player = new YTApi.Player(this.element, {
          width: '100%',
          height: '100%',
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            controls: 0,
            disablekb: 1,
            modestbranding: 1,
            rel: 0,
            iv_load_policy: 3,
            playsinline: 1,
            fs: 0,
            autoplay: 1,
            mute: 1,
            cc_load_policy: this.subtitles ? 1 : 0,
            cc_lang_pref: 'en',
          },
          events: {
            onReady: () => {
              this.ready = true
              // Belt-and-suspenders: make sure autoplay is in the allow list
              // (the API sets it, but we guarantee it for older API builds).
              try {
                const ifr = this.player!.getIframe()
                if (ifr) {
                  const allow = ifr.getAttribute('allow') || ''
                  if (!/autoplay/.test(allow)) {
                    ifr.setAttribute(
                      'allow',
                      (allow ? allow + '; ' : '') +
                        'autoplay; encrypted-media; picture-in-picture',
                    )
                  }
                }
              } catch {
                /* ignore */
              }
              try {
                this.player!.setLoop(true)
                this.player!.setVolume(this.volume)
                if (this.muted) this.player!.mute()
                else this.player!.unMute()
              } catch {
                /* ignore */
              }
              this.cb.onReady?.()
              resolve(this)
            },
            onStateChange: (e) => {
              this.tryPendingSeek()
              this.cb.onStateChange?.(e)
            },
            onError: (e) => {
              this.cb.onError?.(e)
            },
          },
        })
      })
    })
  }

  tune(playlistId: string, index?: number) {
    if (!this.ready || !playlistId) return
    this.currentList = playlistId
    try {
      this.player!.loadPlaylist({
        list: playlistId,
        listType: 'playlist',
        index: index || 0,
        suggestedQuality: 'large',
      })
      this.player!.setLoop(true)
    } catch (e) {
      console.warn('tune failed', e)
      Sentry.captureException(e, { tags: { subsystem: 'youtube' }, extra: { call: 'tune', playlistId } })
    }
  }

  // Play an ad-hoc playlist built from explicit video ids. Works even when
  // the source YouTube playlist is private — only the videos must be public.
  tuneVideos(ids: string[], index?: number) {
    if (!this.ready || !ids || !ids.length) return
    this.currentList = 'videos'
    try {
      this.player!.loadPlaylist({ playlist: ids, index: index || 0, suggestedQuality: 'large' })
      this.player!.setLoop(true)
    } catch (e) {
      console.warn('tuneVideos failed', e)
      Sentry.captureException(e, { tags: { subsystem: 'youtube' }, extra: { call: 'tuneVideos', count: ids.length, index } })
    }
  }

  // CUE variants — queue the content but DON'T autoplay. Used while the set is
  // "locked": the picture holds on the channel bumper until the user whacks the
  // screen, at which point we play + unmute inside the iframe's own activation.
  cuePlaylist(playlistId: string, index?: number) {
    if (!this.ready || !playlistId) return
    this.currentList = playlistId
    try {
      this.player!.cuePlaylist({
        list: playlistId,
        listType: 'playlist',
        index: index || 0,
        suggestedQuality: 'large',
      })
      this.player!.setLoop(true)
    } catch (e) {
      console.warn('cuePlaylist failed', e)
      Sentry.captureException(e, { tags: { subsystem: 'youtube' }, extra: { call: 'cuePlaylist', playlistId } })
    }
  }
  cueVideos(ids: string[], index?: number) {
    if (!this.ready || !ids || !ids.length) return
    this.currentList = 'videos'
    try {
      this.player!.cuePlaylist({ playlist: ids, index: index || 0, suggestedQuality: 'large' })
      this.player!.setLoop(true)
    } catch (e) {
      console.warn('cueVideos failed', e)
      Sentry.captureException(e, { tags: { subsystem: 'youtube' }, extra: { call: 'cueVideos', count: ids.length, index } })
    }
  }

  // Single-video verbs — the basis of SELF-DRIVEN playback (we advance our own
  // queue instead of leaning on YouTube's playlist engine, which is what caps
  // at 200 and breaks autoplay). cue = load paused; load = load + play.
  cueVideoById(id: string, startSeconds = 0) {
    if (!this.ready || !id) return
    this.currentList = 'single'
    try {
      // startSeconds positions the cued (non-playing) frame — used to PRE-SEEK the standby toward
      // the live line so a swap lands on-time. A cued frame shows the thumbnail at that offset;
      // full buffering still begins on play, so the swap's exact seek remains the correctness floor.
      this.player!.cueVideoById(id, startSeconds > 0 ? startSeconds : undefined)
    } catch (e) {
      console.warn('cueVideoById failed', e)
      Sentry.captureException(e, { tags: { subsystem: 'youtube' }, extra: { call: 'cueVideoById', id } })
    }
  }
  loadVideoById(id: string) {
    if (!this.ready || !id) return
    this.currentList = 'single'
    try {
      this.player!.loadVideoById(id)
    } catch (e) {
      console.warn('loadVideoById failed', e)
      Sentry.captureException(e, { tags: { subsystem: 'youtube' }, extra: { call: 'loadVideoById', id } })
    }
  }

  rebuild(options: TubeOptions = {}): Promise<Tube> {
    this.subtitles = Boolean(options.subtitles)
    this.clearPendingSeek()
    const iframe = this.getIframeEl()
    const parent = iframe?.parentElement || this.container
    if (!parent) return Promise.resolve(this)
    try {
      this.player?.destroy()
    } catch {
      /* ignore */
    }
    parent.textContent = ''
    const mount = document.createElement('div')
    parent.appendChild(mount)
    this.element = mount
    this.container = parent
    this.player = null
    this.ready = false
    this.currentList = null
    return this.init()
  }

  private clearPendingSeek() {
    this.pendingSeek = null
    if (this.pendingSeekTimer) window.clearTimeout(this.pendingSeekTimer)
    this.pendingSeekTimer = null
  }

  private tryPendingSeek() {
    if (this.pendingSeek == null || !this.ready || !this.player) return
    // Backstop clamp against YouTube's reported duration when it's known (the
    // primary clamp uses the schedule's known durationSeconds at the call site).
    const duration = this.getDuration()
    const target = duration > 1 ? Math.min(this.pendingSeek, duration - 0.5) : this.pendingSeek
    try {
      this.player.seekTo(Math.max(0, target), true)
    } catch {
      /* ignore */
    }
    if (this.pendingSeekTimer) window.clearTimeout(this.pendingSeekTimer)
    this.pendingSeekTimer = window.setTimeout(() => {
      if (this.pendingSeek == null || !this.ready) return
      const now = this.getCurrentTime()
      if (Math.abs(now - target) < 0.75) {
        this.clearPendingSeek()
        return
      }
      this.tryPendingSeek()
    }, 180)
  }

  restorePlaybackPosition(seconds: number) {
    if (!Number.isFinite(seconds) || seconds <= 0) return
    this.pendingSeek = seconds
    this.tryPendingSeek()
  }

  // How many items the player currently has queued (0 = nothing loaded).
  playlistCount(): number {
    if (!this.ready) return 0
    try {
      return (this.player!.getPlaylist() || []).length
    } catch {
      return 0
    }
  }

  stop() {
    if (!this.ready) return
    try {
      this.player!.stopVideo()
    } catch {
      /* ignore */
    }
    this.currentList = null
  }

  play() {
    try {
      this.player!.playVideo()
    } catch {
      /* ignore */
    }
  }
  pause() {
    try {
      this.player!.pauseVideo()
    } catch {
      /* ignore */
    }
  }

  togglePlay(): boolean | null {
    if (!this.ready) return null
    const st = this.player!.getPlayerState()
    if (st === 1) {
      this.pause()
      return false
    }
    this.play()
    return true
  }

  next() {
    try {
      this.player!.nextVideo()
    } catch {
      /* ignore */
    }
  }
  prev() {
    try {
      this.player!.previousVideo()
    } catch {
      /* ignore */
    }
  }
  playAt(i: number) {
    try {
      this.player!.playVideoAt(i)
    } catch {
      /* ignore */
    }
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(100, v))
    if (!this.ready) return
    try {
      this.player!.setVolume(this.volume)
      if (!this.muted && v > 0 && this.player!.isMuted && this.player!.isMuted()) {
        this.player!.unMute()
      }
    } catch {
      /* ignore */
    }
  }
  mute() {
    this.muted = true
    try {
      this.player!.mute()
    } catch {
      /* ignore */
    }
  }
  unMute() {
    this.muted = false
    try {
      this.player!.unMute()
    } catch {
      /* ignore */
    }
  }
  setSubtitlesEnabled(on: boolean) {
    this.subtitles = on
  }
  isMuted(): boolean {
    if (!this.ready) return true
    try {
      return this.player!.isMuted()
    } catch {
      return true
    }
  }

  getState(): number {
    if (!this.ready) return -1
    try {
      return this.player!.getPlayerState()
    } catch {
      return -1
    }
  }

  seekTo(seconds: number) {
    if (!this.ready) return
    try {
      this.player!.seekTo(Math.max(0, seconds), true)
    } catch {
      /* ignore */
    }
  }

  getCurrentTime(): number {
    if (!this.ready) return 0
    try {
      return this.player!.getCurrentTime() || 0
    } catch {
      return 0
    }
  }

  /** Total length of the loaded video in seconds (0 if unknown / not yet loaded). */
  getDuration(): number {
    if (!this.ready) return 0
    try {
      return this.player!.getDuration() || 0
    } catch {
      return 0
    }
  }

  /** The seek we're still trying to land (for diagnostics on a frame error). */
  peekPendingSeek(): number | null {
    return this.pendingSeek
  }

  // The actual <iframe> DOM node — used to detect a real click landing on the
  // cross-origin player frame (which grants it its own user-activation).
  getIframeEl(): HTMLIFrameElement | null {
    if (!this.ready) return null
    try {
      return this.player!.getIframe()
    } catch {
      return null
    }
  }

  // The id of the playlist/source currently loaded ('videos' for an ad-hoc list).
  getCurrentList(): string | null {
    return this.currentList
  }

  getProgram(): Program | null {
    if (!this.ready) return null
    try {
      const list = this.player!.getPlaylist() || []
      const idx = this.player!.getPlaylistIndex()
      const data = this.player!.getVideoData() || ({} as YT.VideoData)
      return {
        index: idx,
        total: list.length,
        title: data.title || '',
        author: data.author || '',
        videoId: data.video_id || '',
        duration: this.player!.getDuration() || 0,
        time: this.player!.getCurrentTime() || 0,
      }
    } catch {
      return null
    }
  }
}
